<template>
  <section class="d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Resumen fiscal</h2>
        <p class="text-muted mb-0">Fuente de verdad para establecimiento, punto de expedición y serie fiscal activa.</p>
      </div>
      <button class="btn btn-outline-dark btn-sm" @click="loadAll" :disabled="loading">Recargar</button>
    </header>

    <div v-if="errorMessage" class="alert alert-danger mb-0">{{ errorMessage }}</div>

    <!-- CFE-E4: vía principal de configuración fiscal — subir el export de
         FE en vez de cargar timbrado/establecimiento/serie a mano en cuatro
         pantallas distintas (SPEC_CONFIG_FISCAL_POR_EXPORT_v0.1.md). -->
    <div class="card shadow-sm">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-2">
          <div>
            <h3 class="h6 mb-1">Configuración fiscal desde el export de facturación electrónica</h3>
            <small class="text-muted">Reemplaza la carga manual de timbrado, establecimientos, puntos y series.</small>
          </div>
        </div>

        <div v-if="lastUpload" class="text-muted small mb-3">
          Última carga: {{ formatDateTime(lastUpload.generado_en) }}
          <span v-if="lastUpload.filename">· {{ lastUpload.filename }}</span>
          <span v-if="lastUpload.subido_por_nombre || lastUpload.subido_por_usuario">
            · subido por {{ lastUpload.subido_por_nombre || lastUpload.subido_por_usuario }}
          </span>
        </div>
        <div v-else class="text-muted small mb-3">Todavía no se cargó ningún export de configuración fiscal.</div>

        <div v-if="!canManageFiscal" class="alert alert-secondary mb-0">
          Sólo el administrador puede subir o aplicar un export de configuración fiscal.
        </div>

        <template v-else>
          <div class="mb-3">
            <label class="form-label">Archivo de configuración (.json exportado de facturación electrónica)</label>
            <input
              ref="importFileInput"
              class="form-control"
              type="file"
              accept="application/json,.json"
              :disabled="importLoading"
              @change="handleImportFile"
            />
          </div>

          <div v-if="importError" class="alert alert-danger">{{ importError }}</div>

          <div v-if="importPreview" class="border rounded p-3 bg-light">
            <div class="mb-2">
              <strong>Emisor:</strong> {{ importPreview.emisor_id }}
              · <strong>Generado:</strong> {{ formatDateTime(importPreview.generado_en) }}
            </div>

            <div v-if="importPreview.warnings?.length" class="alert alert-warning py-2 mb-2">
              <div v-for="warning in importPreview.warnings" :key="warning.codigo">{{ warning.mensaje }}</div>
            </div>

            <ul class="mb-3 small">
              <li>Establecimientos: {{ importPreview.diff.establecimientos.crear.length }} nuevos, {{ importPreview.diff.establecimientos.actualizar.length }} actualizados, {{ importPreview.diff.establecimientos.sin_cambios.length }} sin cambios</li>
              <li>Puntos de expedición: {{ importPreview.diff.puntos.crear.length }} nuevos, {{ importPreview.diff.puntos.sin_cambios.length }} sin cambios</li>
              <li>Timbrados: {{ importPreview.diff.timbrados.crear.length }} nuevos, {{ importPreview.diff.timbrados.actualizar.length }} actualizados, {{ importPreview.diff.timbrados.sin_cambios.length }} sin cambios</li>
              <li>Series: {{ importPreview.diff.series.crear.length }} nuevas, {{ importPreview.diff.series.sin_cambios.length }} sin cambios</li>
            </ul>

            <div v-if="importPreview.requiere_serie_predeterminada" class="mb-3">
              <label class="form-label">Este export produce más de una serie posible. Elegí cuál es la predeterminada antes de aplicar:</label>
              <select v-model="seriePredeterminadaKey" class="form-select">
                <option value="" disabled>Elegir serie...</option>
                <option
                  v-for="option in serieOptions"
                  :key="option.key"
                  :value="option.key"
                >
                  Timbrado {{ option.timbrado_numero }} · Establecimiento {{ option.establecimiento_codigo }} · Punto {{ option.punto_codigo }}
                </option>
              </select>
            </div>

            <div class="d-flex gap-2">
              <button
                class="btn btn-dark"
                :disabled="importLoading || (importPreview.requiere_serie_predeterminada && !seriePredeterminadaKey)"
                @click="confirmImport"
              >
                {{ importLoading ? 'Aplicando...' : 'Confirmar y aplicar' }}
              </button>
              <button class="btn btn-outline-secondary" type="button" :disabled="importLoading" @click="cancelImportPreview">
                Cancelar
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-md-4">
        <div class="card shadow-sm h-100">
          <div class="card-body">
            <div class="text-muted small">Establecimientos</div>
            <div class="display-6 fw-semibold">{{ establishments.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm h-100">
          <div class="card-body">
            <div class="text-muted small">Timbrados activos</div>
            <div class="display-6 fw-semibold">{{ activeTimbrados }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm h-100">
          <div class="card-body">
            <div class="text-muted small">Series registradas</div>
            <div class="display-6 fw-semibold">{{ series.length }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <h3 class="h6">Contexto fiscal resuelto</h3>
        <div v-if="resolvedContext" class="row g-3 mt-1">
          <div class="col-md-3"><strong>Establecimiento:</strong> {{ resolvedContext.codigo_establecimiento }} · {{ resolvedContext.nombre_establecimiento }}</div>
          <div class="col-md-3"><strong>Punto:</strong> {{ resolvedContext.codigo_punto }} · {{ resolvedContext.nombre_punto }}</div>
          <div class="col-md-3"><strong>Timbrado:</strong> {{ resolvedContext.numero_timbrado }}</div>
          <div class="col-md-3"><strong>Siguiente número:</strong> {{ resolvedContext.siguiente_numero }}</div>
        </div>
        <div v-else class="text-muted">No se pudo resolver todavía el contexto fiscal activo.</div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <h3 class="h6 mb-1">Perfil fiscal operativo</h3>
            <small class="text-muted">Configuración efectiva usada por ventas nuevas y facturación electrónica.</small>
          </div>
          <span class="badge" :class="operationalProfile?.consistency?.ok ? 'bg-success' : 'bg-warning text-dark'">
            {{ operationalProfile?.consistency?.ok ? 'Consistente' : 'Revisar' }}
          </span>
        </div>

        <div v-if="profileWarnings.length" class="alert alert-warning">
          <div v-for="warning in profileWarnings" :key="`${warning.code}-${warning.field}`">
            {{ warning.message }}
          </div>
        </div>

        <form class="row g-3" @submit.prevent="saveOperationalProfile">
          <div class="col-md-6">
            <label class="form-label">URL base FE</label>
            <input v-model="profileForm.integration.base_url" class="form-control" type="text" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Timeout ms</label>
            <input v-model.number="profileForm.integration.timeout_ms" class="form-control" type="number" min="1000" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Ambiente</label>
            <select v-model="profileForm.integration.env" class="form-select">
              <option value="test">test</option>
              <option value="prod">prod</option>
            </select>
          </div>

          <div class="col-md-8">
            <label class="form-label">API key FE</label>
            <input v-model="profileApiKey" class="form-control" type="password" autocomplete="new-password" :placeholder="profileForm.integration.api_key_set ? 'Configurada' : 'Sin configurar'" />
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check">
              <input id="clearApiKey" v-model="profileClearApiKey" class="form-check-input" type="checkbox" />
              <label class="form-check-label" for="clearApiKey">Limpiar API key guardada</label>
            </div>
          </div>

          <div class="col-md-4">
            <label class="form-label">Emisor FE</label>
            <input v-model="profileForm.issuer.emisor_id" class="form-control" type="text" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Actividad económica</label>
            <input v-model="profileForm.issuer.actividad_economica_codigo" class="form-control" type="text" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Perfil de emisión</label>
            <input v-model="profileForm.issuer.emission_profile_code" class="form-control" type="text" />
          </div>

          <div class="col-md-3">
            <label class="form-label">Timbrado</label>
            <input v-model="profileForm.fiscal_series.timbrado_numero" class="form-control" type="text" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Fecha inicio</label>
            <input v-model="profileForm.fiscal_series.timbrado_fecha_inicio" class="form-control" type="date" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Establecimiento</label>
            <input v-model="profileForm.fiscal_series.establecimiento_codigo" class="form-control" type="text" maxlength="3" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Punto</label>
            <input v-model="profileForm.fiscal_series.punto_expedicion_codigo" class="form-control" type="text" maxlength="3" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Siguiente POS</label>
            <input v-model.number="profileForm.fiscal_series.siguiente_numero_pos" class="form-control" type="number" min="1" />
          </div>

          <div class="col-md-3">
            <div class="form-check form-switch">
              <input id="profileEnabled" v-model="profileForm.integration.enabled" class="form-check-input" type="checkbox" />
              <label class="form-check-label" for="profileEnabled">FE habilitada</label>
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-check form-switch">
              <input id="profileContadoEnabled" v-model="profileForm.integration.contado_enabled" class="form-check-input" type="checkbox" />
              <label class="form-check-label" for="profileContadoEnabled">Contado automático</label>
            </div>
          </div>
          <div class="col-md-3">
            <label class="form-label">Modo contado</label>
            <select v-model="profileForm.integration.contado_mode" class="form-select">
              <option value="SYNC">SYNC</option>
              <option value="AUTO">AUTO</option>
              <option value="BATCH">BATCH</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Ticket fiscal</label>
            <select v-model="profileForm.integration.ticket_format" class="form-select">
              <option value="escpos">escpos</option>
              <option value="text">text</option>
            </select>
          </div>

          <div class="col-12">
            <button class="btn btn-dark" :disabled="savingProfile">{{ savingProfile ? 'Guardando...' : 'Guardar perfil fiscal operativo' }}</button>
          </div>
        </form>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <h3 class="h6">Empresa, identidad comercial y fiscal</h3>
        <form class="row g-3 mt-1" @submit.prevent="saveCompany">
          <div class="col-md-4">
            <label class="form-label">Nombre comercial del cliente</label>
            <input v-model="companyForm.nombre_comercial" class="form-control" type="text" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Razón social</label>
            <input v-model="companyForm.razon_social" class="form-control" type="text" />
          </div>
          <div class="col-md-4">
            <label class="form-label">RUC</label>
            <input v-model="companyForm.ruc" class="form-control" type="text" />
          </div>
          <div class="col-md-6">
            <label class="form-label">Logo comercial en sistema URL</label>
            <input v-model="companyForm.logo_comercial_url" class="form-control" type="text" readonly />
          </div>
          <div class="col-md-6">
            <label class="form-label">Logo factura URL</label>
            <input v-model="companyForm.logo_factura_url" class="form-control" type="text" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Cabecera por defecto</label>
            <select v-model="companyForm.ticket_header_mode" class="form-select">
              <option v-for="mode in ticketHeaderModes" :key="mode.value" :value="mode.value">{{ mode.label }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">Ancho ticket</label>
            <select v-model.number="companyForm.ticket_print_width_mm" class="form-select">
              <option :value="80">80mm</option>
              <option :value="58">58mm</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Subir logo comercial</label>
            <input class="form-control" type="file" accept="image/svg+xml,image/png,image/jpeg,image/webp" @change="handleLogoFile" />
            <small class="text-muted">El logo comercial es único por instalación.</small>
          </div>
          <div class="col-md-6">
            <label class="form-label">Logo VENTAX térmico B/N</label>
            <input v-model="companyForm.system_thermal_logo_url" class="form-control" type="text" readonly />
            <small class="text-muted">Preparado para 80mm y 58mm.</small>
          </div>
          <div class="col-md-6">
            <label class="form-label">Logo comercial térmico B/N</label>
            <input v-model="companyForm.logo_comercial_thermal_url" class="form-control" type="text" readonly />
            <small class="text-muted">{{ commercialThermalStatusLabel }}</small>
          </div>
          <div class="col-12">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Cabecera factura</label>
                <select v-model="companyForm.ticket_header_modes_by_document.FACTURA" class="form-select">
                  <option v-for="mode in ticketHeaderModes" :key="`factura-${mode.value}`" :value="mode.value">{{ mode.label }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Cabecera recibo de dinero</label>
                <select v-model="companyForm.ticket_header_modes_by_document.RECIBO_DINERO" class="form-select">
                  <option v-for="mode in ticketHeaderModes" :key="`recibo-${mode.value}`" :value="mode.value">{{ mode.label }}</option>
                </select>
              </div>
            </div>
          </div>
          <div class="col-12" v-if="logoPreviewUrl || companyForm.logo_comercial_url">
            <div class="branding-preview">
              <img :src="logoPreviewUrl || companyForm.logo_comercial_url" alt="Logo comercial" />
              <div>
                <div class="fw-semibold">{{ companyForm.nombre_comercial || 'Nombre comercial' }}</div>
                <div class="text-muted small">Preview de identidad comercial</div>
              </div>
            </div>
          </div>
          <div class="col-12">
            <button class="btn btn-dark" :disabled="savingCompany">{{ savingCompany ? 'Guardando...' : 'Guardar identidad y datos fiscales' }}</button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import maintenanceFiscalService from '@/services/maintenanceFiscalService'
import { hasPermission, getCurrentSucursalIdFromSession } from '@/services/authSessionService'

const loading = ref(false)
const savingCompany = ref(false)
const savingProfile = ref(false)
const errorMessage = ref('')
const establishments = ref([])
const timbrados = ref([])
const series = ref([])
const resolvedContext = ref(null)
const operationalProfile = ref(null)
const profileApiKey = ref('')
const profileClearApiKey = ref(false)
const companyForm = reactive({
  nombre_comercial: '',
  razon_social: '',
  ruc: '',
  logo_sistema_url: '',
  logo_factura_url: '',
  logo_comercial_url: '',
  logo_comercial_thermal_url: '',
  logo_comercial_thermal_status: '',
  system_thermal_logo_url: '',
  ticket_header_mode: 'COMERCIO_VENTAX',
  ticket_header_modes_by_document: {
    FACTURA: 'COMERCIO_VENTAX',
    RECIBO_DINERO: 'SOLO_NOMBRE_COMERCIO'
  },
  ticket_print_width_mm: 80
})
const profileForm = reactive({
  integration: {
    enabled: true,
    base_url: '',
    api_key_set: false,
    timeout_ms: 15000,
    env: 'test',
    contado_enabled: true,
    contado_mode: 'SYNC',
    credito_enabled: true,
    credito_mode: 'SYNC',
    ticket_format: 'escpos'
  },
  issuer: {
    emisor_id: '',
    actividad_economica_codigo: '',
    emission_profile_code: '',
    operational_series: ''
  },
  fiscal_series: {
    establecimiento_codigo: '001',
    punto_expedicion_codigo: '001',
    timbrado_numero: '',
    timbrado_fecha_inicio: '',
    timbrado_fecha_fin: null,
    siguiente_numero_pos: 1,
    predeterminado: true
  }
})
const pendingLogoFile = ref(null)
const logoPreviewUrl = ref('')

// CFE-E4: subida del export de configuración fiscal.
const sucursalId = computed(() => getCurrentSucursalIdFromSession() || 1)
const canManageFiscal = computed(() => hasPermission('gestionar_facturacion_electronica'))
const lastUpload = ref(null)
const importFileInput = ref(null)
const importLoading = ref(false)
const importError = ref('')
const importPreview = ref(null)
const importFilename = ref('')
const importFileContent = ref(null)
const seriePredeterminadaKey = ref('')

const serieOptions = computed(() => {
  const preview = importPreview.value
  if (!preview) return []
  return [...preview.diff.series.crear, ...preview.diff.series.sin_cambios].map((s) => ({
    key: `${s.timbrado_numero}::${s.establecimiento_codigo}::${s.punto_codigo}`,
    ...s
  }))
})

function formatDateTime(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return String(value)
  }
}

async function loadLastUpload() {
  try {
    lastUpload.value = await maintenanceFiscalService.getLastConfigImport(sucursalId.value)
  } catch (error) {
    console.error('Error cargando el último export fiscal cargado', error)
  }
}

function cancelImportPreview() {
  importPreview.value = null
  importFileContent.value = null
  importFilename.value = ''
  importError.value = ''
  seriePredeterminadaKey.value = ''
  if (importFileInput.value) importFileInput.value.value = ''
}

async function handleImportFile(event) {
  const file = event?.target?.files?.[0] || null
  if (!file) return
  importError.value = ''
  importPreview.value = null
  seriePredeterminadaKey.value = ''
  importLoading.value = true
  try {
    const text = await file.text()
    importFileContent.value = text
    importFilename.value = file.name
    const preview = await maintenanceFiscalService.importConfig(
      sucursalId.value,
      { contenido: text, filename: file.name },
      { simular: true }
    )
    importPreview.value = preview
  } catch (error) {
    console.error('Error simulando la carga de configuración fiscal', error)
    importError.value = error.userMessage || error?.response?.data?.error || 'No se pudo leer el archivo de configuración'
    cancelImportPreview()
  } finally {
    importLoading.value = false
  }
}

async function confirmImport() {
  if (!importFileContent.value) return
  importLoading.value = true
  importError.value = ''
  try {
    let seriePredeterminada = null
    if (seriePredeterminadaKey.value) {
      const [timbrado_numero, establecimiento_codigo, punto_codigo] = seriePredeterminadaKey.value.split('::')
      seriePredeterminada = { timbrado_numero, establecimiento_codigo, punto_codigo }
    }
    await maintenanceFiscalService.importConfig(
      sucursalId.value,
      { contenido: importFileContent.value, filename: importFilename.value, serie_predeterminada: seriePredeterminada },
      { simular: false }
    )
    window.alert('Configuración fiscal aplicada')
    cancelImportPreview()
    await loadLastUpload()
    await loadAll()
  } catch (error) {
    console.error('Error aplicando la configuración fiscal', error)
    importError.value = error.userMessage || error?.response?.data?.error || 'No se pudo aplicar la configuración fiscal'
  } finally {
    importLoading.value = false
  }
}

const activeTimbrados = computed(() => timbrados.value.filter((row) => row.activo).length)
const profileWarnings = computed(() => operationalProfile.value?.consistency?.warnings || [])
const commercialThermalStatusLabel = computed(() => {
  if (!companyForm.logo_comercial_url) return 'Sin logo comercial cargado.'
  if (companyForm.logo_comercial_thermal_status === 'READY_MONOCHROME_SVG') return 'Derivado B/N listo para 80mm y 58mm.'
  if (companyForm.logo_comercial_thermal_status === 'PENDING_RASTER_CONVERSION') return 'Pendiente: subir SVG para generar derivado B/N automático.'
  return 'Derivado térmico no disponible.'
})
const ticketHeaderModes = [
  { value: 'COMERCIO_VENTAX', label: 'Logo comercio + VENTAX' },
  { value: 'SOLO_COMERCIO_LOGO', label: 'Sólo logo comercio' },
  { value: 'VENTAX_NOMBRE_COMERCIO', label: 'VENTAX + nombre comercio' },
  { value: 'SOLO_NOMBRE_COMERCIO', label: 'Sólo nombre comercio' }
]

function assignCompany(payload = {}) {
  const modesByDocument = payload.ticket_header_modes_by_document || {}
  companyForm.nombre_comercial = payload.nombre_comercial || ''
  companyForm.razon_social = payload.razon_social || ''
  companyForm.ruc = payload.ruc || ''
  companyForm.logo_sistema_url = payload.logo_sistema_url || ''
  companyForm.logo_factura_url = payload.logo_factura_url || ''
  companyForm.logo_comercial_url = payload.logo_comercial_url || payload.logo_sistema_url || ''
  companyForm.logo_comercial_thermal_url = payload.logo_comercial_thermal_url || ''
  companyForm.logo_comercial_thermal_status = payload.logo_comercial_thermal_status || ''
  companyForm.system_thermal_logo_url = payload.system_thermal_logo_url || ''
  companyForm.ticket_header_mode = payload.ticket_header_mode || 'COMERCIO_VENTAX'
  companyForm.ticket_header_modes_by_document.FACTURA = modesByDocument.FACTURA || companyForm.ticket_header_mode
  companyForm.ticket_header_modes_by_document.RECIBO_DINERO = modesByDocument.RECIBO_DINERO || companyForm.ticket_header_mode
  companyForm.ticket_print_width_mm = Number(payload.ticket_print_width_mm || 80)
}

function assignOperationalProfile(payload = {}) {
  operationalProfile.value = payload || null
  Object.assign(profileForm.integration, {
    ...profileForm.integration,
    ...(payload.integration || {})
  })
  Object.assign(profileForm.issuer, {
    ...profileForm.issuer,
    ...(payload.issuer || {})
  })
  Object.assign(profileForm.fiscal_series, {
    ...profileForm.fiscal_series,
    ...(payload.fiscal_series || {})
  })
  profileApiKey.value = ''
  profileClearApiKey.value = false
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

function handleLogoFile(event) {
  const file = event?.target?.files?.[0] || null
  pendingLogoFile.value = file
  logoPreviewUrl.value = file ? URL.createObjectURL(file) : ''
}

async function loadAll() {
  loading.value = true
  errorMessage.value = ''
  try {
    const [company, profile, establishmentRows, timbradoRows, seriesRows, context] = await Promise.all([
      maintenanceFiscalService.getCompany(),
      maintenanceFiscalService.getOperationalProfile(),
      maintenanceFiscalService.listEstablishments(),
      maintenanceFiscalService.listTimbrados(),
      maintenanceFiscalService.listSeries(),
      maintenanceFiscalService.resolve()
    ])
    assignCompany(company || {})
    assignOperationalProfile(profile || {})
    establishments.value = establishmentRows || []
    timbrados.value = timbradoRows || []
    series.value = seriesRows || []
    resolvedContext.value = context || null
  } catch (error) {
    console.error('Error cargando dashboard fiscal', error)
    errorMessage.value = error.userMessage || 'No se pudo cargar la configuración fiscal'
  } finally {
    loading.value = false
  }
}

async function saveOperationalProfile() {
  savingProfile.value = true
  try {
    const payload = {
      integration: { ...profileForm.integration },
      issuer: { ...profileForm.issuer },
      fiscal_series: { ...profileForm.fiscal_series }
    }
    if (profileApiKey.value) {
      payload.integration.api_key = profileApiKey.value
    }
    if (profileClearApiKey.value) {
      payload.integration.clear_api_key = true
    }
    const saved = await maintenanceFiscalService.updateOperationalProfile(payload)
    assignOperationalProfile(saved || {})
    await loadAll()
    window.alert('Perfil fiscal operativo guardado')
  } catch (error) {
    console.error('Error guardando perfil fiscal operativo', error)
    window.alert(error.userMessage || error?.response?.data?.error || 'No se pudo guardar el perfil fiscal operativo')
  } finally {
    savingProfile.value = false
  }
}

async function saveCompany() {
  savingCompany.value = true
  try {
    let payload = {
      ...companyForm,
      ticket_header_modes_by_document: { ...companyForm.ticket_header_modes_by_document }
    }
    if (pendingLogoFile.value) {
      const upload = await maintenanceFiscalService.uploadCommercialLogo({
        filename: pendingLogoFile.value.name,
        data_url: await fileToDataUrl(pendingLogoFile.value)
      })
      payload.logo_comercial_url = upload?.branding?.commerce?.logo_url || upload?.asset?.variant_screen_url || upload?.asset?.public_url || payload.logo_comercial_url
      pendingLogoFile.value = null
    }
    const saved = await maintenanceFiscalService.updateCompany(payload)
    assignCompany(saved || {})
    logoPreviewUrl.value = ''
    window.alert('Identidad comercial y datos fiscales actualizados')
  } catch (error) {
    console.error('Error guardando datos fiscales', error)
    window.alert(error.userMessage || 'No se pudo guardar la empresa fiscal')
  } finally {
    savingCompany.value = false
  }
}

onMounted(() => {
  loadAll()
  loadLastUpload()
})
</script>

<style scoped>
.branding-preview {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(19, 47, 58, 0.12);
  border-radius: 8px;
  background: #f7fbfd;
}

.branding-preview img {
  max-width: 160px;
  max-height: 70px;
  object-fit: contain;
}
</style>
